import {
  DateFilterModel,
  ISimpleFilterModel,
  NumberFilterModel,
  TextFilterModel,
} from '@ag-grid-community/core';
import { format, parse } from 'date-fns';
import { filter as ramdaFilter } from 'ramda';

import { CONSTANTS } from '../config/constants';

const agGridDateFilterFormat = 'yyyy-MM-dd HH:mm:ss';

interface MultiValueFilterModel extends ISimpleFilterModel {
  values: string[];
}

export enum AgGridFilterFieldType {
  Text = 'text',
  Number = 'number',
  Date = 'date',
  Set = 'set',
}

export enum AgGridFilterOperation {
  Contains = 'contains',
  Equals = 'equals',
  Empty = 'empty',
  InRange = 'inRange',
  LessThan = 'lessThan',
  GreaterThan = 'greaterThan',
  GreaterThanOrEqual = 'greaterThanOrEqual',
  LessThanOrEqual = 'lessThanOrEqual',
  NotContains = 'notContains',
  EndsWith = 'endsWith',
  NotEqual = 'notEqual',
  StartsWith = 'startsWith',
}

export enum FilterOperations {
  contains = 'contains',
  equals = 'equals',
  equalcaseinsensitive = 'equalcaseinsensitive',
  lessthan = 'lessthan',
  greaterthan = 'greaterthan',
  between = 'between',
  blank = 'blank',
  in = 'in',
}

/**
 * Mapping of ag-Grid provided filter operations
 * See: https://www.ag-grid.com/javascript-grid-filter-provided-simple/#filterOptions
 */
export const gridFilterOperationMap: Record<AgGridFilterOperation, FilterOperations> = {
  [AgGridFilterOperation.Contains]: FilterOperations.contains,
  [AgGridFilterOperation.Equals]: FilterOperations.equals,
  // unsupported
  [AgGridFilterOperation.Empty]: FilterOperations.blank,
  [AgGridFilterOperation.GreaterThan]: FilterOperations.blank,
  [AgGridFilterOperation.LessThan]: FilterOperations.blank,
  [AgGridFilterOperation.InRange]: FilterOperations.blank,
  [AgGridFilterOperation.NotContains]: FilterOperations.blank,
  [AgGridFilterOperation.EndsWith]: FilterOperations.blank,
  [AgGridFilterOperation.StartsWith]: FilterOperations.blank,
  [AgGridFilterOperation.NotEqual]: FilterOperations.blank,
  [AgGridFilterOperation.GreaterThanOrEqual]: FilterOperations.blank,
  [AgGridFilterOperation.LessThanOrEqual]: FilterOperations.blank,
};

const toApiDateFormat = (value: string | null): string | null => {
  return value
    ? format(parse(value, agGridDateFilterFormat, new Date()), CONSTANTS.DATE_FORMATS.UI)
    : null;
};

export const mapFilterModel = (filterModel: Record<string, ISimpleFilterModel>): string[] => {
  const notNill = <T>(v: T) => v !== undefined && v !== null;

  const filterParams = Object.entries(filterModel).reduce<string[]>((acc, [field, model]) => {
    const { type, filterType } = model;
    const operation = gridFilterOperationMap[type as AgGridFilterOperation];

    switch (filterType) {
      case AgGridFilterFieldType.Text: {
        const { filter } = model as TextFilterModel;
        const strings = [...acc, [field, operation, filter].join(',')];

        return strings;
      }
      case AgGridFilterFieldType.Date: {
        const { dateFrom, dateTo } = model as DateFilterModel;
        const query = ramdaFilter(notNill, [
          field,
          operation,
          toApiDateFormat(dateFrom),
          toApiDateFormat(dateTo),
        ]);

        return query.length >= 3 ? [...acc, query.join(',')] : acc;
      }
      case AgGridFilterFieldType.Number: {
        const { filter, filterTo } = model as NumberFilterModel;
        const query = ramdaFilter(notNill, [field, operation, filter, filterTo]);

        return query.length >= 3 ? [...acc, query.join(',')] : acc;
      }
      case AgGridFilterFieldType.Set: {
        const { values } = model as MultiValueFilterModel;
        return values.length
          ? [...acc, [field, FilterOperations.in, values.join('|')].join(',')]
          : acc;
      }
    }

    return acc;
  }, []);

  return filterParams;
};
